// Imports ------------------------------------------------------------------->

import * as i18n from '../i18n/I18n'
import { Service, service, inject } from './di'
import {
    Flyer,
    FlyerItem,
    FlyerDetails,
    FlyerItemDetails,
    FlyerCategory,
} from '../definitions/FlyerDefinitions'
import { Coupon, CouponCategory } from '../definitions/CouponDefinitions'
import { AggregateSourceAction } from './definitions/IAnalyticsService'
import { LocationService } from './LocationService'
import { SeoService } from './SeoService'
import MetadataService from './MetadataService'
import { SeoDataService } from './SeoDataService'
import { ConfigService } from './ConfigService'
import { BlackFridayService } from './BlackFridayService'
import { getLocaleFromUrl } from '../i18n/I18n'
import { slugify } from '../utils/slugify'

export type LinkParams = {
    sourceAction?: AggregateSourceAction
    context?: FlyerDetails
    tracked?: boolean
    hostname?: string
}

@service
export class LinkService extends Service {
    @inject config: ConfigService
    @inject location: LocationService
    @inject seo: SeoService
    @inject metadata: MetadataService
    @inject seoDataService: SeoDataService
    @inject blackFriday: BlackFridayService

    execute(href: string, obj: EventTarget, params?: LinkParams) {
        params = { hostname: this.config.HOSTNAME, ...params }
        const target = obj || window
        target.dispatchEvent(
            new CustomEvent('flipp-navigate', {
                bubbles: true,
                cancelable: true,
                detail: {
                    href,
                    params,
                },
            })
        )
    }

    linkTo(obj: any, qp?: { [name: string]: any }): string {
        let href = ''

        if (obj === 'search') {
            return i18n.i18n(`route:search`, { q: qp.q })
            // FIXME [FW-399] <Link> knows too much about each object and i18n routes.
        } else if (obj === 'store') {
            const locale = this.location.locale
            if (qp.name) {
                return qp.location
                    ? `/${locale}/${qp.location}/stores/${qp.name}`
                    : `/${locale}/stores/${qp.name}`
            } else {
                return qp.location
                    ? `/${locale}/${qp.location}/stores`
                    : `/${locale}/stores`
            }
        } else if (obj === 'city_flyers') {
            const postfix =
                this.location.locale === 'en-us' ? 'weekly_ads' : 'flyers'
            return `/${this.location.locale}/${qp.location}/${postfix}`
        } else if (obj === 'blackFriday') {
            const locale = this.location.locale
            return locale === 'en-us'
                ? '/en-us/weekly_ads/black-friday'
                : locale === 'fr-ca'
                  ? '/fr-ca/circulaires/vendredi-fou'
                  : '/en-ca/flyers/black-friday'
        } else if (obj === 'location') {
            const locale = this.location.locale
            return `/${locale}/${qp.location}`
        } else if (obj === 'proudlyCanadian') {
            const locale = this.location.locale
            return `/${locale}/flyers/proudly-canadian`
        } else if (typeof obj === 'string') {
            const str = obj.split('?')
            const route = str[0]
            const param = str.length === 2 ? str[1] : ''
            href = i18n.i18n(`route:${route}`, { prefix: '', param })
        } else if (
            obj instanceof Flyer ||
            (obj instanceof FlyerDetails && obj.flyer)
        ) {
            const flyer =
                obj instanceof Flyer ? (obj as Flyer) : (obj.flyer as Flyer)
            qp = { ...qp, postal_code: this.location.postalCode }

            if (qp && qp['item_id']) {
                href = this.metadata.getItemLink(
                    `${qp['item_id']}-${slugify(flyer.getName())}`
                )
                qp = null
            } else if (flyer && flyer.city_state) {
                // store page
                href = this.getFlyerLink(
                    flyer,
                    `/${getLocaleFromUrl()}/${flyer.city_state}`
                )
                qp = null
            } else {
                href = this.getFlyerLink(flyer, this.getFlyerLocationPrefix())
            }
        } else if (
            obj instanceof FlyerItemDetails ||
            obj instanceof FlyerItem
        ) {
            qp = { ...qp, postal_code: this.location.postalCode }

            const name = (obj as FlyerItem).flyerName || obj.name
            const merchantName = (obj as FlyerItem).merchantName
            href = i18n.i18n('route:item', {
                slug: `${obj.id}-${slugify(merchantName)}-${slugify(name)}`,
                prefix: this.getLocationPrefix(),
            })
        } else if (obj instanceof Coupon) {
            const coupon = obj as Coupon
            href = this.getCouponLink(coupon, this.getLocationPrefix())
        } else if (obj instanceof CouponCategory) {
            const category = obj as CouponCategory

            href = i18n.i18n('route:coupons', {
                category:
                    category.name === 'All Deals'
                        ? ''
                        : slugify(i18n.i18n(category.name)),
                prefix: this.getLocationPrefix(),
            })
        } else if (obj instanceof FlyerCategory) {
            qp = { ...qp, postal_code: this.location.postalCode }

            const category = obj as FlyerCategory
            const slugified = slugify(i18n.i18n(category.name))
            if (
                this.blackFriday.isEnabled() &&
                slugified === this.config.BF_CATEGORY
            ) {
                return this.blackFriday.getHostLink()
            }
            href = i18n.i18n('route:listing', {
                category: category.name === 'All Flyers' ? '' : slugified,
                prefix: this.getLocationPrefix(),
                sort: qp['sort'] ? qp['sort'] : '',
            })
        }

        if (qp && Object.keys(qp).length !== 0) {
            href +=
                '?' +
                Object.getOwnPropertyNames(qp)
                    .map((name) => `${name}=${encodeURIComponent(qp[name])}`)
                    .join('&')
        }

        return href
    }

    getFlyerLink(flyer: Flyer, prefix: string = null): string {
        return i18n.i18n('route:browse', {
            slug: `${flyer.id}-${slugify(flyer.merchant)}-${slugify(flyer.name)}`,
            prefix,
        })
    }

    getCouponLink(coupon: Coupon, prefix: string = null): string {
        return i18n.i18n('route:coupon', {
            slug: coupon.id + (coupon.brand ? `-${slugify(coupon.brand)}` : ''),
            prefix,
        })
    }

    getMerchantHref(name: string, locale: any = this.metadata.getLocale()) {
        return `/${locale}/store/${slugify(name)}`
    }

    private getLocationPrefix(): string {
        const locale = this.location.locale
        const inCity = this.seo.inCity
        return inCity ? `/${locale}/${inCity}`.toLowerCase() : `/${locale}`
    }

    private getFlyerLocationPrefix(): string {
        const city = this.location.getInCity()
        if (!city) {
            return null
        }
        return `/${this.location.locale}/${this.location.getInCity()}`.toLowerCase()
    }
}
